/* @jsxRuntime automatic */
/* @jsxImportSource @superweb/css */

import { useLocale } from "@superweb/intl";
import { LinkButton, useIsMobile, useTypo, useUiColors } from "@superweb/ui";
import { useFlag } from "@superweb/flags";
import { cssFns } from "@superweb/css";

import { useQuery } from "@fleet/api";

import { useLogAction } from "#internal/analytics/logActions";
import { searchParamTypes as t, useSearchParams } from "#internal/router-new";
import { AppLayout, NotFound, Offer, OfferDeprecated } from "#internal/ui";
import { useBrand } from "#internal/router/useBrand";
import { Message, useMessage } from "#internal/intl";
import SerbiaOfferPng from "#internal/images/serbia-offer.png";
import SerbiaOfferWebp from "#internal/images/serbia-offer.webp";
import SerbiaOfferAvif from "#internal/images/serbia-offer.avif";

import TruckWithRoadAvif from "../../images/truck-with-road.avif";
import TruckWithRoad from "../../images/truck-with-road.svg";
import TruckWithRoadWebp from "../../images/truck-with-road.webp";
import CarWithRoadAvif from "../../images/car-with-road.avif";
import CarWithRoad from "../../images/car-with-road.svg";
import CarWithRoadWebp from "../../images/car-with-road.webp";
import YandexEndPageAvif from "../../images/yandex-end-page.avif";
import YandexEndPage from "../../images/yandex-end-page.svg";
import YandexEndPageWebp from "../../images/yandex-end-page.webp";

import { useIsIos } from "../use-is-ios";

const deliveryProfessionIds = new Set([
  "cargo_courier_on_foot",
  "cargo_courier_on_bicycle",
  "cargo_courier_on_scooter",
  "cargo_courier_on_moto",
  "cargo_courier_on_car",
  "cargo_courier_on_truck",
]);

const Content = ({
  refId,
  sessionId,
  employmentType,
  professionId,
  isIframe,
  isMultipleProfessions,
}: {
  refId: string;
  sessionId: string;
  employmentType?: "selfemployed" | "individual_entrepreneur" | "park_employee";
  professionId?: string;
  isIframe: boolean;
  isMultipleProfessions?: boolean;
}) => {
  const flag = useFlag();
  const locale = useLocale();
  const message = useMessage();
  const brand = useBrand();

  const typo = useTypo();
  const uiColors = useUiColors();

  const isMobile = useIsMobile();
  const isIos = useIsIos();

  const logActionSubmit = useLogAction("submit");
  const logAction = useLogAction("contact");

  const { response } = useQuery({
    url: "/fleet-forms/selfreg/v1/park/info",
    method: "GET",
    headers: {
      language: locale.toString(),
      "x-session-id": sessionId,
    },
    query: {
      ref_id: refId,
      employment_type: employmentType,
      profession_id: professionId,
    },
  });
  const parkInfo = response.status === 200 ? response.body : undefined;

  const yangoBrand = brand === "yango";

  const downloadButtonText = isMobile
    ? {
        android: message({
          id: "fb31089e-473b-46b7-88fe-a3484607a054",
          context: "Yandex forms. Download app page. Download button. Andorid",
          default: "Android",
        }),
        ios: message({
          id: "3adb9386-7333-4d19-b93e-bdf8cb5425ab",
          context: "Yandex forms. Download app page. Download button. IOS",
          default: "iOS",
        }),
      }
    : {
        android: message({
          id: "b7235b85-13ae-4360-a77b-80ce5de2fa88",
          context: "Yandex forms. Download app page. Download button. Andorid",
          default: "Android app",
        }),
        ios: message({
          id: "2f3dc2be-a838-46ec-8239-ff6b30af4fc9",
          context: "Yandex forms. Download app page. Download button. IOS",
          default: "iOS app",
        }),
      };

  const enableSingleDownloadButton = flag({
    id: "d6146bf2-3621-448f-9484-165709fc981d",
    description: "Enable single button for downloading Yandex Pro app",
  });

  if (!parkInfo) {
    return <NotFound />;
  }

  const iosAppLink = parkInfo.link_to_pro.ios_app;
  const androidAppLink = parkInfo.link_to_pro.android_app;
  const showSingleDownloadButton =
    enableSingleDownloadButton && isMobile && iosAppLink;

  const isDelivery =
    !isMultipleProfessions &&
    deliveryProfessionIds.has(
      parkInfo.profession_settings_list[0]?.profession_id ?? "",
    );
  const offerType = isMultipleProfessions
    ? "multiple"
    : isDelivery
      ? "delivery"
      : "taxi";

  const enableNewOffer = flag({
    id: "169ec9b9-0e61-4f2e-b8c5-13048eccca82",
    description: "Enable new offer",
  });
  const enableSerbiaOfferImage =
    parkInfo.park_id === "1a1d6c5f1ad7423481a1bc8a8c5d09db";

  const imageSrc = yangoBrand
    ? isDelivery
      ? { avif: TruckWithRoadAvif, webp: TruckWithRoadWebp, svg: TruckWithRoad }
      : { avif: CarWithRoadAvif, webp: CarWithRoadWebp, svg: CarWithRoad }
    : { avif: YandexEndPageAvif, webp: YandexEndPageWebp, svg: YandexEndPage };

  const imageSize = isDelivery
    ? isMobile
      ? { width: 600, height: 280 }
      : { width: 630, height: 300 }
    : isMobile
      ? { width: 300, height: 260 }
      : { width: 330, height: 280 };

  return (
    <AppLayout
      title={
        isIframe
          ? undefined
          : message({
              id: "7cfa0111-c8cb-42f7-81f2-0d713dfb0354",
              context: "Yango forms. Download app page. Title",
              default: "Registration complete",
            })
      }
      partnerName={parkInfo.partner_name}
      content={
        <div
          css={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            ...cssFns.padding("16px"),
            minWidth: "0",
            height: "100%",
            boxSizing: "border-box",
          }}
        >
          <picture>
            <source src={imageSrc.avif} type="image/avif" />
            <source src={imageSrc.webp} type="image/webp" />
            <img
              src={imageSrc.svg}
              alt={message({
                id: "8937619b-8f5a-428c-b80b-dddd8d6743b3",
                context: "Yango forms. Download app page. Image",
                default: "Vehicle",
              })}
              width={imageSize.width}
              height={imageSize.height}
            />
          </picture>
          <span
            css={{
              paddingBlockStart: "30px",
              paddingBlockEnd: "10px",
              ...typo({
                level: "title4",
                weight: "medium",
                density: "normal",
              }),
              color: uiColors.text,
            }}
          >
            {yangoBrand ? (
              <Message
                id="3d650b62-0e56-4c51-83fa-93335c4a21d2"
                context="Yango forms. Download app page. Title"
                default="Registration complete!"
              />
            ) : (
              <Message
                id="08809d4f-e551-4613-ac5a-c74d44228eba"
                context="Yandex forms. Download app page. Title"
                default="Request submitted"
              />
            )}
          </span>
          <span
            css={{
              paddingBlockEnd: "30px",
              ...typo({
                level: "body2",
                weight: "regular",
                density: "tight",
              }),
              color: uiColors.text,
              textAlign: "center",
              maxWidth: "600px",
            }}
          >
            {yangoBrand ? (
              <Message
                id="394aeef8-8789-4daf-b206-22f2b0cf455c"
                context="Yango forms. Download app page. Description"
                default="You completed registration and can start earning now. Please download the app and follow the instructions in it."
              />
            ) : (
              <Message
                id="b1820425-2964-4529-976e-c36b0fe8d657"
                context="Yandex forms. Download app page. Description"
                default="It will be processed soon. For now, download the Yandex Pro app to get further instructions and go online"
              />
            )}
          </span>
          {yangoBrand ? (
            <LinkButton
              text={message({
                id: "ebb57285-c6bc-447f-953e-12dffeedc1aa",
                context: "Yango forms. Download app page. Download button",
                default: "Download the app for free",
              })}
              variant="action"
              link={{
                external: true,
                href: androidAppLink,
              }}
              onPress={() => logActionSubmit("download_app")}
            />
          ) : (
            <div
              css={{
                display: "grid",
                gridTemplateColumns:
                  iosAppLink && !showSingleDownloadButton ? "1fr 1fr" : "1fr",
                columnGap: "8px",
              }}
            >
              {showSingleDownloadButton ? (
                <LinkButton
                  text={message({
                    id: "144594e4-cdc8-4ce5-ab2d-c59246aeab20",
                    context: "Yandex forms. Download app page. Download button",
                    default: "Download app",
                  })}
                  link={{
                    external: true,
                    href: isIos ? iosAppLink : androidAppLink,
                  }}
                  onPress={() => logActionSubmit("download_app")}
                />
              ) : (
                <>
                  {iosAppLink && (
                    <LinkButton
                      text={downloadButtonText["ios"]}
                      link={{
                        external: true,
                        href: iosAppLink,
                      }}
                      onPress={() => logActionSubmit("download_app")}
                    />
                  )}
                  <LinkButton
                    text={downloadButtonText["android"]}
                    link={{
                      external: true,
                      href: androidAppLink,
                    }}
                    onPress={() => logActionSubmit("download_app")}
                  />
                </>
              )}
            </div>
          )}
          {parkInfo.country_code === "RU" ? (
            <div
              css={{
                marginBlockStart: "16px",
              }}
            >
              <LinkButton
                link={{
                  external: true,
                  href: "https://pro.yandex.ru/ru-ru/knowledge-base/taxi/new-driver/emulation",
                }}
                text={message({
                  id: "b6dcef8e-5e24-4a6b-9f16-a9914a091173",
                  context: "Yandex forms. Download app page. Education link",
                  default: "Training",
                })}
              />
            </div>
          ) : null}
        </div>
      }
      offer={
        isIframe ? undefined : enableNewOffer ? (
          <Offer
            offerText={parkInfo.promo_text}
            image={
              enableSerbiaOfferImage
                ? {
                    avif: SerbiaOfferAvif,
                    png: SerbiaOfferPng,
                    webp: SerbiaOfferWebp,
                    height: "130px",
                    width: "178px",
                    padding: "24px",
                  }
                : undefined
            }
            type={offerType}
            onPhonePress={() =>
              logAction("phone_click_v2", {
                params: { stage: "register" },
              })
            }
            onMessengerPress={() =>
              logAction("messenger_click_v2", {
                params: { stage: "register" },
              })
            }
            phone={parkInfo.phone}
            messageLink={parkInfo.message_link}
          />
        ) : (
          <OfferDeprecated
            parkId={parkInfo.park_id}
            offerText={parkInfo.promo_text}
            isDelivery={isDelivery}
            onPhonePress={() =>
              logAction("phone_click_v2", { params: { stage: "download_app" } })
            }
            onMessengerPress={() =>
              logAction("messenger_click_v2", {
                params: { stage: "download_app" },
              })
            }
            phone={parkInfo.phone}
            messageLink={parkInfo.message_link}
          />
        )
      }
    />
  );
};

export const DownloadAppPage = ({ isIframe }: { isIframe: boolean }) => {
  const [
    {
      ref_id,
      sessionId,
      is_multiple_professions: isMultipleProfessions,
      employment_type_id: employmentType,
      profession_id: professionId,
    },
  ] = useSearchParams({
    ref_id: t.string,
    sessionId: t.string,
    is_multiple_professions: t.boolean,
    employment_type_id: t.enumString([
      "selfemployed",
      "individual_entrepreneur",
      "park_employee",
    ] as const),
    profession_id: t.string,
  });

  if (!ref_id || !sessionId) {
    return <NotFound />;
  }

  return (
    <Content
      refId={ref_id}
      sessionId={sessionId}
      employmentType={employmentType}
      professionId={professionId}
      isIframe={isIframe}
      isMultipleProfessions={isMultipleProfessions}
    />
  );
};
